import { CircularProgress, Stack, Typography } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CheckInCircleIcon,
  DisconnectIcon,
  HubSpotIcon,
  SalesForceIcon,
} from "../../../images";
import { CustomButton } from "components/ui/CustomButton";
import { ActionType, CrmTypes } from "types";
import { CRMData, CRMListItem, IntegrationInfo } from "types/apiTypes";
import { useLazyGetCRMListQuery } from "api";
import { useGetCRMUrlMutation } from "api/companyApi";
import { getErrorMessage } from "utils/getErrorMessage";
import { CustomToast } from "../../CustomToast";
import { SyncCRMModal } from "../SyncCRMModal/SyncCRMModal";
import { DisconnectCRMBtn } from "../../buttons/DisconnectCRMBtn";
import { ReuseTooltip } from "../../ReuseTooltip";

const Method = ({
  icon,
  title,
  text,
  btnHandler,
  isLoading,
  isSynced,
  isDisabled,
  isUpdate,
  isConnected = true,
  onDisconnect,
}: {
  icon: ReactNode;
  title: string;
  text: string;
  btnHandler: () => void;
  isLoading: boolean;
  isSynced: boolean;
  isDisabled: boolean;
  isUpdate?: boolean;
  isConnected?: boolean;
  onDisconnect: () => void;
}) => {
  return (
    <Stack
      sx={{
        gap: "16px",
        border: "1px solid #D9D9D9",
        borderRadius: "8px",
        padding: "16px",
        flexDirection: isUpdate ? "row" : "column",
        justifyContent: isUpdate ? "space-between" : "flex-start",
        ...(isSynced && isConnected && { paddingRight: 0 }),
      }}
    >
      <Stack gap="8px" justifyContent="center">
        <Stack flexDirection="row" gap="4px" alignItems="center">
          {icon}
          <Typography
            component="span"
            variant="button"
            color="custom.textDarkBlue"
          >
            {title}
          </Typography>
        </Stack>
        {!isUpdate && (
          <Typography component="span" variant="caption" color="#4C545B">
            {text}
          </Typography>
        )}
      </Stack>
      {isSynced ? (
        <Stack flexDirection="row" gap="4px" alignItems="center">
          <ReuseTooltip
            text={
              !isConnected
                ? `${title} is disconnected. Click ‘Re-sync’ to reconnect and upload data.`
                : ""
            }
            hideChildren={false}
          >
            <Typography component="span" variant="button" color="#4C545B">
              {isConnected ? "Synced" : "Disconnected"}
            </Typography>
          </ReuseTooltip>

          {isConnected ? (
            <Stack flexDirection="row" alignItems="center">
              <CheckInCircleIcon />
              <DisconnectCRMBtn
                crm={title.toLowerCase() as CrmTypes}
                onDisconnect={onDisconnect}
              />
            </Stack>
          ) : (
            <DisconnectIcon />
          )}
        </Stack>
      ) : (
        <CustomButton
          onClick={btnHandler}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          type="button"
          customSize="fit"
          sx={{
            height: "32px",
            borderColor: "#1D3E70",
          }}
          disabled={isDisabled || isLoading}
        >
          Sync Data{" "}
          {isLoading && <CircularProgress size={20} color="inherit" />}
        </CustomButton>
      )}
    </Stack>
  );
};

type UploadMethod = CrmTypes | "";

export const CRMUpdateMethod = ({
  synced,
  setDataSource,
  campaign_id,
  setIsVisible,
  crmIntegration,
  actionType,
}: {
  synced?: UploadMethod;
  setDataSource: (data: CRMData | null) => void;
  campaign_id: string;
  setIsVisible?: (isVisible: boolean) => void;
  crmIntegration?: IntegrationInfo;
  actionType: ActionType;
}) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [openModal, setOpenModal] = useState<UploadMethod>("");
  const [crm, setCrm] = useState<UploadMethod>("");
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>("");
  const [crmList, setCrmList] = useState<CRMListItem[]>([]);

  useEffect(() => {
    setIsVisible && setIsVisible(!!openModal);
  }, [openModal]);

  const [getCRMUrl] = useGetCRMUrlMutation();

  const [getCRMListQuery, { isFetching }] = useLazyGetCRMListQuery();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const crm = urlParams.get("crm");
    const actionType = urlParams.get("actionType");

    if (crm) {
      actionType !== "connect" && setCrm(crm as UploadMethod);
      navigate(pathname, {
        replace: true,
      });
    }
  }, []);

  useEffect(() => {
    if (crm) {
      getCRMListQuery(crm)
        .unwrap()
        .then((data) => {
          setCrmList(data || []);
          setOpenModal(crm);
          setCrm("");
        })
        .catch(async (err) => {
          if (err?.status === 428) {
            getCRMUrl({
              campaign_id,
              action_type: actionType,
              redirect_path: location.pathname,
              crm,
            })
              .unwrap()
              .then((data) => {
                window.location.href = data.url;
              })
              .catch((err) =>
                toast.error(
                  <CustomToast
                    type="error"
                    text={
                      getErrorMessage(err).globalError ||
                      "Something went wrong. Please try again"
                    }
                    title="Connection Failed."
                  />,
                ),
              );
          } else {
            toast.error(
              <CustomToast
                type="error"
                text={
                  getErrorMessage(err).globalError ||
                  "The company data upload failed."
                }
                title="Upload Failed"
              />,
            );
          }
        });
    }
  }, [crm]);

  const setDataSourceHandler = (data: CRMData) => {
    setDataSource(data);
    setUploadMethod(data.crm);
  };

  return (
    <Stack flexDirection={campaign_id ? "column" : "row"} gap="8px">
      {[
        {
          title: "HubSpot",
          integration: "hubspot",
          text: "Automatically import your contacts and accounts from HubSpot CRM.",
          icon: <HubSpotIcon />,
        },
        {
          title: "Salesforce",
          integration: "salesforce",
          text: "Quickly import contacts and accounts from Salesforce CRM.",
          icon: <SalesForceIcon />,
        },
      ].map(({ integration, text, icon, title }) => (
        <Method
          key={integration}
          isUpdate={!!campaign_id}
          isLoading={crm === integration}
          icon={icon}
          title={title}
          btnHandler={() => setCrm(integration as UploadMethod)}
          text={text}
          isSynced={
            synced === integration ||
            uploadMethod === integration ||
            crmIntegration?.integration === integration
          }
          isDisabled={
            !!uploadMethod ||
            (!!crmIntegration && crmIntegration?.integration !== integration)
          }
          isConnected={!!crmIntegration?.is_connected || synced === integration}
          onDisconnect={() =>
            setDataSourceHandler({ list_id: undefined, crm: "" })
          }
        />
      ))}

      {openModal && (
        <SyncCRMModal
          crmList={crmList}
          openModal={openModal}
          onClose={() => setOpenModal("")}
          setDataSource={setDataSourceHandler}
          setOpenModal={setCrm}
          isLoading={isFetching}
        />
      )}
    </Stack>
  );
};
